/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { DialogCustomizable, Icon, IconButton, Text, Notification } from '../index';
import styles from './dialog-info.module.scss';
import { useOutsideClick } from '../../hooks';

const baseClass = 'dialog-info';

export type DialogInfoField = {
  label: string;
  value: string;
  info?: { text: string; infoButtonAriaLabel: string; closeButtonAriaLabel: string };
  isVisible: boolean;
};

export type DialogInfoProps = {
  className?: string;
  onClose: (e: React.UIEvent) => void;
  closeButtonAriaLabel: string;
  outsideClick: () => void;
  isOpen: boolean;
  legalText: string;
  headerText: string;
  fields: DialogInfoField[];
};

type PopUpNotificationType = Pick<DialogInfoField, 'info'>;

const PopUpNotification: React.FC<Required<PopUpNotificationType>> = ({ info }) => {
  const [open, setOpen] = useState(false);
  const notificationRef = useRef(null);
  const classes = classnames(styles[`${baseClass}__notification`], {
    [styles[`${baseClass}__notification--visible`]]: open
  });

  useOutsideClick(notificationRef, () => setOpen(false));

  return (
    <div className={styles[`${baseClass}__notification-wrapper`]}>
      <IconButton
        iconName="info-legal--small"
        variant="ghost"
        size="200"
        onClick={() => setOpen(!open)}
        ariaLabel={info.infoButtonAriaLabel}
      />
      <Notification
        ref={notificationRef}
        noIcon
        text={info.text}
        className={classes}
        variant="informational"
        ariaHidden={!open}
        closeButton={{ ariaLabel: info.closeButtonAriaLabel, onClick: () => setOpen(false) }}
      />
    </div>
  );
};

export const DialogInfo: React.FC<DialogInfoProps> = ({
  isOpen,
  onClose,
  outsideClick,
  legalText,
  headerText,
  fields,
  className,
  closeButtonAriaLabel
}) => {
  const contentRef = useRef(null);

  return (
    <DialogCustomizable
      isOpen={isOpen}
      outsideClick={outsideClick}
      className={classnames(styles[baseClass], 'setting--inverted', className)}
    >
      <div className={styles[`${baseClass}__wrapper`]}>
        <IconButton
          iconName="close-without-circle--small"
          ariaLabel={closeButtonAriaLabel}
          onClick={onClose}
          variant="ghost"
          className={styles[`${baseClass}__close`]}
        />
        <div className={styles[`${baseClass}__title`]}>
          <Icon name="info-legal--small" className={styles[`${baseClass}__header-icon`]} />
          <Text variant="legal-100" className={styles[`${baseClass}__title-paragraph`]}>
            {headerText}
          </Text>
        </div>
        <div className={styles[`${baseClass}__content`]} ref={contentRef}>
          <div className={styles[`${baseClass}__fieldset`]}>
            {fields.map(
              (field, idx) =>
                field.isVisible && (
                  <div className={styles[`${baseClass}__fieldset-child`]} key={idx}>
                    <div className={styles[`${baseClass}__fieldset-child__label`]}>
                      <Text variant="legal-100" className={styles[`${baseClass}__legal-label`]}>
                        {field.label}
                      </Text>
                      {field.info && (
                        <PopUpNotification
                          info={{
                            text: field.info.text,
                            closeButtonAriaLabel: field.info.closeButtonAriaLabel,
                            infoButtonAriaLabel: field.info.infoButtonAriaLabel
                          }}
                        />
                      )}
                    </div>
                    <Text>{field.value}</Text>
                  </div>
                )
            )}
          </div>
          <div className={styles[`${baseClass}__legal`]}>
            <Text variant="legal-100" className={styles[`${baseClass}__legal-paragraph`]}>
              {legalText}
            </Text>
          </div>
        </div>
      </div>
    </DialogCustomizable>
  );
};
